import { useEffect, useState } from "react";
import { useAxios } from "@/hooks/use-axios/useAxios";
import useInspectionId from "@/store/useInspectionId";
import routes from "@/assets/routes/routes";
import ProblemMessage from "@/components/problem-message/ProblemMessage";
import { type InspectionData } from "./inspection.d";
import InspectionButtonsHeader from "./components/InspectionButtonsHeader";
import TopAlertMessage from "@/components/alert-message/TopAlertMessage";
import ClosedInspectionInfo from "./components/ClosedInspectionInfo";
import CurrentPhase from "./components/current-phase/CurrentPhase";
import InspectionView from "./InpectionView";
import { usePhase } from "@/store/usePhase";
import CloseInspectionForm from "./components/CloseInspectionForm";
import CanPhaseClose from "./components/CanPhaseClose";
import { useQuery } from "@tanstack/react-query";
import { getInspectionId } from "@/lib/services/inspections";
import { useParams } from "react-router-dom";
import type { IInspectionDetails } from "@/types/types";
import InpectionDetails from "./components/details/InpectionDetails";
import { useUserStore } from "@/store/useUser";
import NotFoundInspection from "./components/NotFoundInspection";

const Inspection = () => {
  const { isSupervisor } = useUserStore();
  const { inspectionId } = useParams() as { inspectionId: string };
  const { request, buildRoute } = useAxios();
  const {
    inspectionData,
    isNewInspectionId,
    snStand,
    updateIsNewInspectionId,
    clearInpectionStatus,
    updateInspectionData,
  } = useInspectionId();
  const { setCurrentPhase, clearPhaseStatus } = usePhase();
  const [error, setError] = useState<string>("");
  const [closeInspectionForm, setCloseInspectionForm] = useState(false);

  const {
    data: inspection,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["inspection", inspectionId],
    queryFn: () => getInspectionId({ id: Number(inspectionId) }),
    enabled: !!inspectionId,
    retry: false,
  });

  const closeError = () => {
    setError("");
  };

  const updateStore = (newDataInpsection: IInspectionDetails) => {
    updateInspectionData(newDataInpsection);
    setCurrentPhase(newDataInpsection.phase);
  };

  const closeInspectionPhase = async (
    phaseId: number,
    esnOut?: undefined | string,
  ) => {
    try {
      if (error) setError("");
      const url = buildRoute(routes.updatePhase, { inspectionId, phaseId });
      const result = await request({
        url,
        body: {
          esnOut,
        },
        method: "PATCH",
      });
      if (result) {
        updateStore(result);
        if (closeInspectionForm) setCloseInspectionForm(false);
      }
    } catch (error) {
      console.error({ error });
      if (closeInspectionForm) setCloseInspectionForm(false);
      setError("noPosibleSaveChange");
    }
  };

  const handleAskCloseInspection = () => {
    setCloseInspectionForm((prev) => !prev);
  };

  useEffect(() => {
    if (inspection) {
      updateStore(inspection);
    }
  }, [inspection]);

  useEffect(() => {
    return () => {
      clearInpectionStatus();
      clearPhaseStatus();
    };
  }, []);

  if (isLoading) {
    return <p>Cargando</p>;
  }

  if (isError) {
    return <NotFoundInspection />;
  }

  if (!inspectionData) {
    return <></>;
  }

  return (
    <div
      className="grid grid-rows-[auto_auto_1fr] h-full min-h-0 max-w-full"
      data-testid={"inpection-data"}
    >
      <div className="w-full flex flex-col">
        <div className="w-full pb-2 border-b border-b-ib-gray-lightest bg-ib-gray-lightest-2 pt-4">
          <div className="px-4 md:px-14 w-full flex flex-col gap-y-4">
            <InspectionButtonsHeader id={inspectionData.id} />
            <InpectionDetails
              inspectionData={inspectionData as InspectionData}
            />
          </div>
        </div>
        {inspectionData?.endDate ? (
          <ClosedInspectionInfo data={inspectionData as InspectionData} />
        ) : (
          <CurrentPhase />
        )}
      </div>
      {!isSupervisor && (
        <CanPhaseClose
          askCloseInspection={handleAskCloseInspection}
          closeInspectionPhase={closeInspectionPhase}
        />
      )}

      <InspectionView inspectionData={inspectionData as InspectionData} />
      {closeInspectionForm ? (
        <CloseInspectionForm
          cancel={handleAskCloseInspection}
          closeInspection={closeInspectionPhase}
        />
      ) : null}
      {error.length > 0 ? (
        <ProblemMessage error={error} closeMessage={closeError} />
      ) : null}
      {isNewInspectionId ? (
        <TopAlertMessage
          titleLabel="success"
          textLabel="inspectionCreated"
          textData={[snStand]}
          formatType="feedback"
          contentType="success"
          formattedText
          closeMessage={() => updateIsNewInspectionId(false)}
        />
      ) : null}
    </div>
  );
};

export default Inspection;
